import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const ARCHETYPES = ["explorer","skeptic","dreamer","tinkerer","storyteller"] as const;
const AVATAR_STYLES = ["bottts","adventurer","shapes","identicon","fun-emoji","thumbs"] as const;

export type PublicProfile = {
  id: string;
  username: string | null;
  display_name: string | null;
  bio: string | null;
  interests: string[] | null;
  occupation: string | null;
  country: string | null;
  archetype: string | null;
  avatar_seed: string | null;
  avatar_style: string | null;
  created_at: string;
};

const profileUpdateSchema = z.object({
  username: z.string().toLowerCase().trim().min(3).max(24).regex(/^[a-z0-9_]+$/).nullable().optional(),
  display_name: z.string().trim().max(60).nullable().optional(),
  bio: z.string().trim().max(280).nullable().optional(),
  interests: z.array(z.string().trim().min(1).max(40)).max(3).optional(),
  occupation: z.string().trim().max(60).nullable().optional(),
  country: z.string().trim().max(60).nullable().optional(),
  archetype: z.enum(ARCHETYPES).nullable().optional(),
  avatar_seed: z.string().trim().max(64).nullable().optional(),
  avatar_style: z.enum(AVATAR_STYLES).optional(),
  is_public: z.boolean().optional(),
});

export const updateMyProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => profileUpdateSchema.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    // Username uniqueness pre-check (the unique index also enforces this).
    if (data.username) {
      const { data: existing } = await (supabase as any)
        .from("profiles")
        .select("id")
        .ilike("username", data.username)
        .neq("id", userId)
        .maybeSingle();
      if (existing) return { ok: false, error: "Username already taken" } as const;
    }
    const { error } = await (supabase as any)
      .from("profiles")
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq("id", userId);
    if (error) return { ok: false, error: error.message } as const;
    return { ok: true } as const;
  });

export const getMyProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const sel = "id, username, display_name, avatar_url, avatar_seed, avatar_style, bio, interests, occupation, country, archetype, is_public, created_at";
    let { data } = await (context.supabase as any)
      .from("profiles").select(sel).eq("id", context.userId).maybeSingle();
    if (!data) {
      // Create row on first read so the editor/quiz can render.
      await (context.supabase as any).from("profiles").insert({ id: context.userId });
      const r = await (context.supabase as any).from("profiles").select(sel).eq("id", context.userId).maybeSingle();
      data = r.data;
    }
    return data as (PublicProfile & { is_public: boolean; avatar_url: string | null }) | null;
  });

// Public read - uses publishable client (RLS lets anyone read where is_public = true).
export const getPublicProfile = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ username: z.string().trim().toLowerCase().min(3).max(24) }).parse(d))
  .handler(async ({ data }) => {
    const sb = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { data: row } = await (sb as any)
      .from("profiles")
      .select("id, username, display_name, bio, interests, occupation, country, archetype, avatar_seed, avatar_style, created_at")
      .ilike("username", data.username)
      .eq("is_public", true)
      .maybeSingle();
    if (!row) return null;
    // Pull badges (public).
    const { data: badgeRows } = await (sb as any)
      .from("user_badges").select("badge_id, earned_at").eq("user_id", row.id);
    const ids = (badgeRows ?? []).map((b: any) => b.badge_id);
    let badges: { slug: string; name: string; icon: string | null; earned_at: string }[] = [];
    if (ids.length > 0) {
      const { data: defs } = await (sb as any).from("badges").select("id, slug, name, icon").in("id", ids);
      const map = new Map<string, any>((defs ?? []).map((d: any) => [d.id, d]));
      badges = (badgeRows ?? []).map((b: any) => {
        const d = map.get(b.badge_id);
        return d ? { slug: d.slug as string, name: d.name as string, icon: (d.icon as string | null) ?? null, earned_at: b.earned_at as string } : null;
      }).filter(Boolean) as any[];
    }
    // Pull public curiosity stats (score / streak / discoveries / quizzes).
    const { data: cur } = await (sb as any)
      .from("user_curiosity")
      .select("score, streak_days, discoveries_viewed, quizzes_completed")
      .eq("user_id", row.id)
      .maybeSingle();
    const stats = {
      score: (cur?.score as number) ?? 0,
      streak_days: (cur?.streak_days as number) ?? 0,
      discoveries_viewed: (cur?.discoveries_viewed as number) ?? 0,
      quizzes_completed: (cur?.quizzes_completed as number) ?? 0,
    };
    return { profile: row as PublicProfile, badges, stats };
  });
